import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FilterIcon, ShoppingBagIcon, StarIcon, GlobeIcon, InstagramIcon, ChevronDownIcon, MapPinIcon, TruckIcon, ChevronUpIcon, BoxIcon, CheckCircleIcon, FlameIcon, HeartIcon, TrendingUpIcon, CalendarIcon, ShieldCheckIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
import SaveToFavorites from '../components/UI/SaveToFavorites';
import { useDarkMode } from '../context/DarkModeContext';
import { useUserRole } from '../context/UserRoleContext';
import useDarkClasses from '../hooks/useDarkClasses';
import useRoleTheme from '../hooks/useRoleTheme';
import { SearchWithAutocomplete } from '../components/UI/SearchWithAutocomplete';
import ViewModeToggle from '../components/UI/ViewModeToggle';
// Mock data for merch businesses (Preserved)
const merchBusinesses = [{
  id: '1',
  name: 'WhimsyReads',
  slug: 'whimsy-reads',
  logo: 'https://images.unsplash.com/photo-1586892477838-2b96e85e0f96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Handcrafted bookmarks, book sleeves, and reading accessories inspired by fantasy worlds and beloved book series.',
  categories: ['Bookmarks', 'Reading Accessories', 'Book Sleeves'],
  productCount: 32,
  rating: 4.8,
  reviewCount: 120,
  location: 'Portland, OR',
  country: 'USA',
  shipping: 'Worldwide',
  tags: ['fantasy', 'handmade', 'eco-friendly'],
  verified: true,
  totalSales: 2340,
  memberSince: 2021,
  featuredProducts: [{
    id: '101',
    name: 'Hand-painted Fantasy Bookmarks',
    image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 15.99,
    badge: 'best-seller' as const
  }],
  social: {
    website: 'https://whimsy-reads.com',
    instagram: 'whimsy_reads',
    etsy: 'WhimsyReads'
  }
}, {
  id: '2',
  name: 'LitCandleCo',
  slug: 'lit-candle-co',
  logo: 'https://images.unsplash.com/photo-1544301679-a814c10cd399?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1543157145-f78c636d023d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Book-inspired soy candles with scents that transport you to fictional worlds. Perfect for creating a cozy reading atmosphere.',
  categories: ['Candles', 'Home Fragrance', 'Gift Sets'],
  productCount: 45,
  rating: 4.9,
  reviewCount: 250,
  location: 'Chicago, IL',
  country: 'USA',
  shipping: 'US & Canada',
  tags: ['soy wax', 'book scents', 'eco-friendly'],
  verified: true,
  totalSales: 5120,
  memberSince: 2020,
  featuredProducts: [{
    id: '201',
    name: "Book Lover Soy Candle - 'Mystery Novel'",
    image: 'https://images.unsplash.com/photo-1602874801007-aa87ff42a45e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 22.5,
    badge: 'most-loved' as const
  }],
  social: {
    website: 'https://litcandleco.com',
    instagram: 'lit_candle_co',
    etsy: 'LitCandleCo'
  }
}, {
  id: '3',
  name: 'PageTurnerTotes',
  slug: 'page-turner-totes',
  logo: 'https://images.unsplash.com/photo-1535585209827-a15fcdbc4c2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1544967082-d9d25d867d66?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Eco-friendly tote bags, pouches, and accessories featuring literary quotes and book-inspired designs.',
  categories: ['Tote Bags', 'Apparel', 'Accessories'],
  productCount: 28,
  rating: 4.7,
  reviewCount: 90,
  location: 'Austin, TX',
  country: 'USA',
  shipping: 'Worldwide',
  tags: ['eco-friendly', 'cotton', 'literary quotes'],
  verified: false,
  totalSales: 870,
  memberSince: 2023,
  featuredProducts: [{
    id: '301',
    name: "Cotton Tote Bag - 'So Many Books, So Little Time'",
    image: 'https://images.unsplash.com/photo-1544967082-d9d25d867d66?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 18.0,
    badge: 'trending' as const
  }],
  social: {
    website: 'https://pageturner-totes.com',
    instagram: 'pageturner_totes',
    etsy: 'PageTurnerTotes'
  }
}, {
  id: '4',
  name: 'QuillAndInkArt',
  slug: 'quill-and-ink-art',
  logo: 'https://images.unsplash.com/photo-1603827457577-609e6f42a45e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1532153354457-5fbe1a3bb0b4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Vintage-inspired bookplates, ex libris stamps, and custom literary art prints for book lovers and collectors.',
  categories: ['Bookplates', 'Art Prints', 'Stationery'],
  productCount: 60,
  rating: 5.0,
  reviewCount: 60,
  location: 'Boston, MA',
  country: 'USA',
  shipping: 'Worldwide',
  tags: ['vintage', 'custom', 'printable'],
  verified: true,
  totalSales: 1580,
  memberSince: 2019,
  featuredProducts: [{
    id: '401',
    name: 'Custom Bookplates - Vintage Style',
    image: 'https://images.unsplash.com/photo-1603827457577-609e6f42a45e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 10.0,
    badge: 'most-loved' as const
  }],
  social: {
    website: 'https://quillandinkart.com',
    instagram: 'quill_and_ink',
    etsy: 'QuillAndInkArt'
  }
}, {
  id: '5',
  name: 'Beyond The Binding',
  slug: 'beyond-the-binding',
  logo: 'https://images.unsplash.com/photo-1607799279861-4dd421887fb3?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: "Bookish & Spice's merchandise store featuring licensed bookish apparel, accessories, and collectibles for romance readers and book lovers.",
  categories: ['Apparel', 'Accessories', 'Collectibles', 'Book Boxes'],
  productCount: 45,
  rating: 4.9,
  reviewCount: 320,
  location: 'Miami, FL',
  country: 'USA',
  shipping: 'US Only',
  tags: ['romance', 'bookish merch', 'licensed products'],
  verified: true,
  totalSales: 8750,
  memberSince: 2020,
  featuredProducts: [{
    id: '501',
    name: 'Spicy Reads Book Lover T-Shirt',
    image: 'https://images.unsplash.com/photo-1503342394128-c104d54dba01?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 28.99,
    badge: 'best-seller' as const
  }],
  social: {
    website: 'https://bookishandspice.com.au/collections/all-licensed-merch',
    instagram: 'bookish_and_spice',
    tiktok: '@bookishandspice'
  }
}, {
  id: '6',
  name: 'DarkAndDisturbed',
  slug: 'dark-and-disturbed',
  logo: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1505681336446-a2f55aed8e8d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Quarterly subscription box featuring dark academia, gothic, and horror literature paired with exclusive themed merchandise.',
  categories: ['Book Boxes', 'Horror', 'Gothic', 'Subscription'],
  productCount: 8,
  rating: 4.8,
  reviewCount: 175,
  location: 'Salem, MA',
  country: 'USA',
  shipping: 'US & Canada',
  tags: ['horror', 'gothic', 'dark academia'],
  verified: true,
  totalSales: 3200,
  memberSince: 2022,
  featuredProducts: [{
    id: '601',
    name: 'Dark & Disturbed Quarterly Subscription',
    image: 'https://images.unsplash.com/photo-1621786030484-4c855eed6974?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 54.99,
    badge: 'trending' as const
  }],
  social: {
    website: 'https://darkanddisturbed.com',
    instagram: 'dark_and_disturbed',
    tiktok: '@darkanddisturbed'
  }
}, {
  id: '7',
  name: 'FantasyRealmCrate',
  slug: 'fantasy-realm-crate',
  logo: 'https://images.unsplash.com/photo-1578950435899-d1c1bf932b1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Bi-monthly subscription box for fantasy lovers featuring a new fantasy novel, exclusive merchandise, and collectibles from magical worlds.',
  categories: ['Book Boxes', 'Fantasy', 'Subscription'],
  productCount: 10,
  rating: 4.7,
  reviewCount: 230,
  location: 'Denver, CO',
  country: 'USA',
  shipping: 'Worldwide',
  tags: ['fantasy', 'sci-fi', 'collectibles'],
  verified: false,
  totalSales: 4100,
  memberSince: 2021,
  featuredProducts: [{
    id: '701',
    name: 'Fantasy Realm Bi-Monthly Subscription',
    image: 'https://images.unsplash.com/photo-1544816155-12df9643f363?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 49.99,
    badge: 'best-seller' as const
  }],
  social: {
    website: 'https://fantasyrealm-crate.com',
    instagram: 'fantasy_realm_crate',
    tiktok: '@fantasyrealm'
  }
}, {
  id: '8',
  name: 'LiteraryThreads',
  slug: 'literary-threads',
  logo: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Book-themed apparel including t-shirts, sweatshirts, and accessories featuring literary quotes, book covers, and author tributes.',
  categories: ['Clothing', 'Apparel', 'Accessories'],
  productCount: 75,
  rating: 4.6,
  reviewCount: 410,
  location: 'Seattle, WA',
  country: 'USA',
  shipping: 'Worldwide',
  tags: ['apparel', 'literary quotes', 'book lover'],
  verified: true,
  totalSales: 12400,
  memberSince: 2019,
  featuredProducts: [{
    id: '801',
    name: 'Classic Literature Quote T-Shirt',
    image: 'https://images.unsplash.com/photo-1503342394128-c104d54dba01?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 28.99,
    badge: 'best-seller' as const
  }],
  social: {
    website: 'https://literarythreads.com',
    instagram: 'literary_threads',
    tiktok: '@literarythreads'
  }
}, {
  id: '9',
  name: 'BookishTreasuresSydney',
  slug: 'bookish-treasures-sydney',
  logo: 'https://images.unsplash.com/photo-1526280760714-f9e8b26f318f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Handcrafted bookish treasures and literary gifts made with love in Sydney, Australia.',
  categories: ['Bookmarks', 'Jewelry', 'Gifts'],
  productCount: 42,
  rating: 4.9,
  reviewCount: 156,
  location: 'Sydney',
  country: 'Australia',
  shipping: 'Worldwide',
  tags: ['handmade', 'literary', 'unique'],
  verified: true,
  totalSales: 2890,
  memberSince: 2021,
  featuredProducts: [{
    id: '901',
    name: 'Australian Authors Bookmark Collection',
    image: 'https://images.unsplash.com/photo-1598346762291-aee88549193f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 12.99,
    badge: 'most-loved' as const
  }],
  social: {
    website: 'https://bookishtreasures.com.au',
    instagram: 'bookish_treasures_syd',
    etsy: 'BookishTreasuresSyd'
  }
}, {
  id: '10',
  name: 'LondonBookLovers',
  slug: 'london-book-lovers',
  logo: 'https://images.unsplash.com/photo-1571331627305-b3c24197a15b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1520443240718-fce21901db79?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'British literature-inspired gifts, teas, and accessories for the sophisticated book lover.',
  categories: ['Tea Sets', 'Stationery', 'Home Decor'],
  productCount: 38,
  rating: 4.7,
  reviewCount: 124,
  location: 'London',
  country: 'Britain',
  shipping: 'Worldwide',
  tags: ['british', 'classic literature', 'tea'],
  verified: true,
  totalSales: 1950,
  memberSince: 2022,
  featuredProducts: [{
    id: '1001',
    name: 'Jane Austen Tea Collection',
    image: 'https://images.unsplash.com/photo-1563911892437-1feda0179e1b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 24.99,
    badge: 'trending' as const
  }],
  social: {
    website: 'https://londonbooklovers.co.uk',
    instagram: 'london_book_lovers',
    etsy: 'LondonBookLovers'
  }
}, {
  id: '11',
  name: 'KiwiBookCrafts',
  slug: 'kiwi-book-crafts',
  logo: 'https://images.unsplash.com/photo-1516876437184-593fda40c542?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1507699622108-4be3abd695ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'New Zealand-inspired bookish crafts featuring local authors, landscapes, and Māori culture.',
  categories: ['Book Art', 'Crafts', 'Indigenous'],
  productCount: 29,
  rating: 4.8,
  reviewCount: 87,
  location: 'Wellington',
  country: 'New Zealand',
  shipping: 'Worldwide',
  tags: ['kiwi', 'māori', 'handcrafted'],
  verified: false,
  totalSales: 640,
  memberSince: 2023,
  featuredProducts: [{
    id: '1101',
    name: 'NZ Authors Wooden Bookmark Set',
    image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 18.5,
    badge: 'trending' as const
  }],
  social: {
    website: 'https://kiwibookcrafts.co.nz',
    instagram: 'kiwi_book_crafts',
    etsy: 'KiwiBookCrafts'
  }
}, {
  id: '12',
  name: 'EuropeanBookTreasures',
  slug: 'european-book-treasures',
  logo: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1517154421773-0529f29ea451?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Curated bookish merchandise from across Europe, featuring multiple languages and cultural traditions.',
  categories: ['Multilingual', 'Cultural', 'Collectibles'],
  productCount: 64,
  rating: 4.6,
  reviewCount: 142,
  location: 'Berlin',
  country: 'Europe',
  shipping: 'Worldwide',
  tags: ['multilingual', 'european', 'cultural'],
  verified: true,
  totalSales: 3670,
  memberSince: 2020,
  featuredProducts: [{
    id: '1201',
    name: 'European Classics Tote Collection',
    image: 'https://images.unsplash.com/photo-1544967082-d9d25d867d66?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 32.0,
    badge: 'most-loved' as const
  }],
  social: {
    website: 'https://europeanbooktreasures.eu',
    instagram: 'european_book_treasures',
    etsy: 'EuropeanBookTreasures'
  }
}, {
  id: '13',
  name: 'TorontoBookishGoods',
  slug: 'toronto-bookish-goods',
  logo: 'https://images.unsplash.com/photo-1544816155-12df9643f363?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1526314114033-349ef6f72220?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Canadian literature-inspired gifts and accessories, featuring maple syrup scented bookmarks!',
  categories: ['Canadian Lit', 'Gifts', 'Scented Items'],
  productCount: 35,
  rating: 4.7,
  reviewCount: 98,
  location: 'Toronto',
  country: 'Canada',
  shipping: 'North America',
  tags: ['canadian', 'maple', 'literary'],
  verified: false,
  totalSales: 920,
  memberSince: 2023,
  featuredProducts: [{
    id: '1301',
    name: 'Maple Scented Bookmark Set',
    image: 'https://images.unsplash.com/photo-1598346762291-aee88549193f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 14.99,
    badge: 'trending' as const
  }],
  social: {
    website: 'https://torontobookishgoods.ca',
    instagram: 'toronto_bookish',
    etsy: 'TorontoBookishGoods'
  }
}, {
  id: '14',
  name: 'BangkokBookDesigns',
  slug: 'bangkok-book-designs',
  logo: 'https://images.unsplash.com/photo-1528698827591-e19ccd7bc23d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1528181304800-259b08848526?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Thai-inspired bookish merchandise featuring local literature, art, and cultural elements.',
  categories: ['Thai Design', 'Stationery', 'Art'],
  productCount: 31,
  rating: 4.8,
  reviewCount: 76,
  location: 'Bangkok',
  country: 'Thailand',
  shipping: 'Worldwide',
  tags: ['thai', 'asian literature', 'handmade'],
  verified: true,
  totalSales: 1120,
  memberSince: 2022,
  featuredProducts: [{
    id: '1401',
    name: 'Thai Literature Notebook Collection',
    image: 'https://images.unsplash.com/photo-1531346878377-a5be20888e57?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    price: 16.5,
    badge: 'most-loved' as const
  }],
  social: {
    website: 'https://bangkokbookdesigns.com',
    instagram: 'bangkok_book_designs',
    etsy: 'BangkokBookDesigns'
  }
}];
type ViewMode = 'grid' | 'list';
// Badge config for featured products
const getBadgeConfig = (badge: string, darkMode: boolean) => {
  switch (badge) {
    case 'best-seller':
      return {
        label: 'Best Seller',
        icon: FlameIcon,
        colors: darkMode ? 'bg-amber-900/40 text-amber-300 border-amber-700/50' : 'bg-amber-50 text-amber-700 border-amber-200'
      };
    case 'most-loved':
      return {
        label: 'Most Loved',
        icon: HeartIcon,
        colors: darkMode ? 'bg-rose-900/40 text-rose-300 border-rose-700/50' : 'bg-rose-50 text-rose-700 border-rose-200'
      };
    case 'trending':
      return {
        label: 'Trending',
        icon: TrendingUpIcon,
        colors: darkMode ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700/50' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
      };
    default:
      return {
        label: 'Featured',
        icon: StarIcon,
        colors: darkMode ? 'bg-indigo-900/40 text-indigo-300 border-indigo-700/50' : 'bg-indigo-50 text-indigo-700 border-indigo-200'
      };
  }
};
const formatSalesCount = (count: number) => {
  if (count >= 10000) return `${(count / 1000).toFixed(1)}k`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count.toString();
};
const MerchBusinessProfiles = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isReader
  } = useUserRole();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedShipping, setSelectedShipping] = useState<string[]>([]);
  const [expandedBoxId, setExpandedBoxId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  // Sidebar states
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Extract unique values for filters
  const allCategories = Array.from(new Set(merchBusinesses.flatMap(b => b.categories))).sort();
  const allCountries = Array.from(new Set(merchBusinesses.map(b => b.country))).sort();
  const allShipping = Array.from(new Set(merchBusinesses.map(b => b.shipping))).sort();
  // Filter logic
  const filteredBusinesses = merchBusinesses.filter(business => {
    const matchesSearch = searchTerm === '' || business.name.toLowerCase().includes(searchTerm.toLowerCase()) || business.description.toLowerCase().includes(searchTerm.toLowerCase()) || business.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategories.length === 0 || business.categories.some(cat => selectedCategories.includes(cat));
    const matchesCountry = selectedCountries.length === 0 || selectedCountries.includes(business.country);
    const matchesShipping = selectedShipping.length === 0 || selectedShipping.includes(business.shipping);
    return matchesSearch && matchesCategory && matchesCountry && matchesShipping;
  });
  // Handlers
  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  const toggleCountry = (country: string) => {
    setSelectedCountries(prev => prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]);
  };
  const toggleShipping = (option: string) => {
    setSelectedShipping(prev => prev.includes(option) ? prev.filter(s => s !== option) : [...prev, option]);
  };
  const toggleExpand = (id: string) => {
    setExpandedBoxId(expandedBoxId === id ? null : id);
  };
  const resetFilters = () => {
    setSelectedCategories([]);
    setSelectedCountries([]);
    setSelectedShipping([]);
    setSearchTerm('');
  };
  const filterSections: FilterSection[] = [{
    id: 'categories',
    title: 'Categories',
    type: 'checkbox',
    options: allCategories.map(cat => ({
      value: cat,
      label: cat
    })),
    value: selectedCategories,
    onChange: val => toggleCategory(val)
  }, {
    id: 'countries',
    title: 'Countries',
    type: 'checkbox',
    options: allCountries.map(c => ({
      value: c,
      label: c
    })),
    value: selectedCountries,
    onChange: val => toggleCountry(val)
  }, {
    id: 'shipping',
    title: 'Shipping',
    type: 'checkbox',
    options: allShipping.map(s => ({
      value: s,
      label: s
    })),
    value: selectedShipping,
    onChange: val => toggleShipping(val)
  }];
  return <div className={`min-h-screen ${dk.page} ${dk.textPrimary}`} data-id="element-9760" data-mp="1hdg4z0q9o0o">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9761" data-mp="3tibcu4cwb5g0">
        {/* Page Header */}
        <div className="mb-8" data-id="element-9762" data-mp="3oks1bc7l16rr">
          <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-9763" data-mp="3om35ve8uqijx">
            Merchandise Business Profiles
          </h1>
          <p className={`${dk.textMuted} max-w-3xl`} data-id="element-9764" data-mp="3etxdi3eotz47">
            Discover unique book-related merchandise from independent businesses
            and creators. From bookmarks to apparel, find the perfect items to
            enhance your reading experience.
          </p>
        </div>

        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-6" data-id="element-9765" data-mp="30u5vlrl1vykx">
          <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-md transition-colors ${dk.card} hover:${dk.surfaceMuted}`} data-id="element-9766" data-mp="30wikkop8797h">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-9767" data-mp="v1pw8smsijol" />
            <span data-id="element-9768" data-mp="v0yb6bqtk4ck">Filters</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-9769" data-mp="10013cvlpag34">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-9770" data-mp="l2sc01wedcbe">
            <FilterSidebar title="Filters" sections={filterSections} onReset={resetFilters} data-id="element-9771" data-mp="c4gbn85qb05r" />
          </FilterSidebarWrapper>

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-id="element-9772" data-mp="26ejyrsh15xvz">
            {/* Search and View Toggle */}
            <div className={`rounded-xl shadow-sm p-4 mb-8 ${dk.card}`} data-id="element-9773" data-mp="2bey8deya5fw4">
              <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-9774" data-mp="2lkpq22ln9uoe">
                <div className="relative flex-1" data-id="element-9775" data-mp="2gl3b9oq646dh">
                  <SearchWithAutocomplete value={searchTerm} onChange={setSearchTerm} placeholder="Search businesses by name, category, or description..." suggestions={[]} data-id="element-9776" data-mp="2gigv50p7oijn" />
                </div>
                <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} data-id="element-9777" data-mp="348b8djdg094f" />
              </div>
            </div>

            {/* Section Header */}
            <div className="flex items-center justify-between mb-6" data-id="element-9778" data-mp="3dbc9t6rd5c1r">
              <div className="flex items-center gap-2" data-id="element-9779" data-mp="3d8hprgc9ht8v">
                <div className={`p-1.5 rounded-lg ${rt.bgMedium} ${rt.textPrimary}`} data-id="element-9780" data-mp="39ak1qgu89d72">
                  <ShoppingBagIcon className="h-5 w-5" data-id="element-9781" data-mp="1iyvxppbvz3oz" />
                </div>
                <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-9782" data-mp="1ewj1psmxyk5e">
                  Merch Businesses
                </h2>
              </div>
              <div className={`flex text-sm font-medium rounded-lg p-1 ${dk.surfaceMuted}`} data-id="element-9783" data-mp="3iz2n58gfu5al">
                {['All', 'Popular', 'Top Rated'].map(tab => {
                const id = tab.toLowerCase().replace(' ', '-');
                const isActive = activeTab === id;
                return <button key={id} onClick={() => setActiveTab(id)} className={`px-3 py-1.5 rounded-md transition-all ${isActive ? `${dk.card} shadow-sm ${rt.textPrimary}` : `${dk.textMuted} hover:${dk.textPrimary}`}`} data-id="element-9784" data-mp="seifjpavk5g">
                      {tab}
                    </button>;
              })}
              </div>
            </div>

            {/* Results Grid/List */}
            <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6' : 'grid-cols-1 gap-4'}`} data-id="element-9785" data-mp="oiijbjb3t5sr">
              {filteredBusinesses.map(business => {
              // GRID VIEW CARD
              if (viewMode === 'grid') {
                return <div key={business.id} className={`group rounded-xl overflow-hidden border transition-all duration-200 hover:shadow-md hover:-translate-y-1 ${dk.card}`} data-id="element-9786" data-mp="20162mujipy3i">
                      {/* Fixed Height Image Area */}
                      <div className="h-48 relative bg-gray-200" data-id="element-9787" data-mp="2nra34t1ethqa">
                        <img src={business.banner} alt={business.name} className="w-full h-full object-cover" data-id="element-9788" data-mp="2iypb9x22depe" />

                        {/* Overlay Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" data-id="element-9789" data-mp="3bhmkeuijoqtj"></div>

                        {/* Shipping Badge (Top Left) */}
                        <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full border border-white/10 flex items-center gap-1" data-id="element-9790" data-mp="2ez5i3fh2t86b">
                          {business.shipping === 'Worldwide' ? 'Worldwide' : business.shipping}
                        </div>

                        {/* Product Count Badge (Top Right) */}
                        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full border border-white/10" data-id="element-9791" data-mp="32rckowdeabe6">
                          {business.productCount} Products
                        </div>

                        {/* Save to Favorites (Bottom Right) */}
                        <div className="absolute bottom-3 right-3" data-id="element-9792" data-mp="1db3h9fhp7sn1">
                          <SaveToFavorites itemId={business.id} size="sm" className="bg-black/40 backdrop-blur-sm hover:bg-black/60 text-white border-white/20" data-id="element-9793" data-mp="1ibiso1if6q2d" />
                        </div>

                        {/* Logo Overlay (Bottom Left) */}
                        <div className="absolute bottom-3 left-3" data-id="element-9794" data-mp="1jq26dylvn3b9">
                          <img src={business.logo} alt={`${business.name} logo`} className="w-16 h-16 rounded-lg border-2 border-white object-cover shadow-md" data-id="element-9795" data-mp="1eud59qu7lecu" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4" data-id="element-9796" data-mp="w226a82oe0k0">
                        {/* Category Label */}
                        <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${rt.textPrimary}`} data-id="element-9797" data-mp="8cq2jnq4akp3">
                          {business.categories[0]}
                        </div>

                        {/* Name */}
                        <Link to={`/vendor/${business.slug}`} className={`block text-lg font-bold mb-2 ${rt.textHover} transition-colors truncate ${dk.textPrimary}`} data-id="element-9798" data-mp="2ssjtv5kgwhe0">
                          <span className="inline-flex items-center gap-1.5" data-id="element-9799" data-mp="2ivf8v92cyrys">
                            {business.name}
                            {business.verified && <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-blue-500" data-id="element-9800" data-mp="3bijhrp4knqfu" />}
                          </span>
                        </Link>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1.5 mb-3 h-6 overflow-hidden" data-id="element-9801" data-mp="32hl7wq7gcopc">
                          {business.categories.slice(0, 3).map((cat, idx) => <span key={idx} className={`text-[10px] px-2 py-0.5 rounded-full border ${dk.badgeNeutral} border-transparent`} data-id="element-9802" data-mp="1r2ohpczj7tge" data-mpi={idx}>
                              {cat}
                            </span>)}
                        </div>

                        {/* Location */}
                        <div className={`flex items-center text-xs mb-4 ${dk.textMuted}`} data-id="element-9803" data-mp="kd3ou10lluhm">
                          <GlobeIcon className="h-3 w-3 mr-1" data-id="element-9804" data-mp="pbdig76ocn14" />
                          {business.location}, {business.country}
                        </div>

                        {/* Divider */}
                        <div className={`h-px w-full mb-3 ${dk.divider}`} data-id="element-9805" data-mp="yd4ml7lkglsv"></div>

                        {/* Bottom Row: Rating & Reviews */}
                        <div className="flex items-center justify-between" data-id="element-9806" data-mp="303y8sdq2819u">
                          <div className="flex items-center text-xs font-medium text-amber-400" data-id="element-9807" data-mp="3062khhu7a4ko">
                            <StarIcon className="h-3.5 w-3.5 fill-current mr-1" data-id="element-9808" data-mp="354m8dnsctqfo" />
                            {business.rating}
                            <span className={`ml-1 ${dk.textMuted}`} data-id="element-9809" data-mp="79raz3uci3lh">
                              ({business.reviewCount} reviews)
                            </span>
                          </div>

                          {/* Book Box Indicator */}
                          {business.categories.includes('Book Boxes') && <Link to="/book-boxes" className={`text-[10px] flex items-center ${rt.accentLink}`} data-id="element-9810" data-mp="v5vncfrcqm0k">
                              <BoxIcon className="h-3 w-3 mr-1" data-id="element-9811" data-mp="13y7397ouar2a" />
                              Book Boxes
                            </Link>}
                        </div>
                      </div>
                    </div>;
              }
              // LIST VIEW CARD
              const isExpanded = expandedBoxId === business.id;
              return <div key={business.id} className={`rounded-xl border transition-all ${dk.card}`} data-id="element-9812" data-mp="1skwkws585eba">
                    {/* Main Row */}
                    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4 relative" data-id="element-9813" data-mp="1ilnto71qudsm">
                      {/* Save Button (Top Right) */}
                      <div className="absolute top-4 right-4 sm:static sm:order-last" data-id="element-9814" data-mp="19u5d3v0vxvz0">
                        <SaveToFavorites itemId={business.id} size="sm" className={`${dk.surfaceMuted} hover:${dk.surface} ${dk.textMuted}`} data-id="element-9815" data-mp="1eklwrwvaudtt" />
                      </div>

                      {/* Thumbnail */}
                      <div className="relative flex-shrink-0" data-id="element-9816" data-mp="quhr7jyjw406">
                        <img src={business.logo} alt={business.name} className={`w-16 h-16 rounded-lg object-cover border ${dk.border}`} data-id="element-9817" data-mp="qx47q93f5tuj" />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0 pr-8 sm:pr-0" data-id="element-9818" data-mp="iwo6x49n4avd">
                        <div className="flex items-center flex-wrap gap-2 mb-1" data-id="element-9819" data-mp="e20qlsosimrv">
                          <Link to={`/vendor/${business.slug}`} className={`text-lg font-bold hover:underline ${dk.textPrimary}`} data-id="element-9820" data-mp="93h5tvlxnh13">
                            <span className="inline-flex items-center gap-1.5" data-id="element-9821" data-mp="296da7oka5f">
                              {business.name}
                              {business.verified && <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-blue-500" data-id="element-9822" data-mp="3mc3ocqkclmqj" />}
                            </span>
                          </Link>
                          <span className={`${dk.textMuted}`} data-id="element-9823" data-mp="1kkqle71xxox4">|</span>
                          <span className={`text-sm ${dk.textSecondary}`} data-id="element-9824" data-mp="1t0xxlxlwxovw">
                            {business.categories[0]}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm" data-id="element-9825" data-mp="21szuxn0oqp1y">
                          <div className="flex items-center gap-1 text-amber-400" data-id="element-9826" data-mp="21v2wn4b7a6mi">
                            <StarIcon className="h-3.5 w-3.5 fill-current" data-id="element-9827" data-mp="26rkz1xj25svo" />
                            <span className={dk.textSecondary} data-id="element-9828" data-mp="1a2vh677fl09p">
                              {business.rating}
                            </span>
                            <span className={`text-xs ${dk.textMuted}`} data-id="element-9829" data-mp="1e58lcr6j4q4d">
                              ({business.reviewCount})
                            </span>
                          </div>
                          <div className={`flex items-center ${dk.textMuted}`} data-id="element-9830" data-mp="1nzidq8vh1of3">
                            <MapPinIcon className="h-3.5 w-3.5 mr-1" data-id="element-9831" data-mp="2uh5l4paid696" />
                            {business.location}, {business.country}
                          </div>
                          <div className={`flex items-center ${dk.textMuted}`} data-id="element-9832" data-mp="29rtet80miupr">
                            <TruckIcon className="h-3.5 w-3.5 mr-1" data-id="element-9833" data-mp="24u1epqpts2ah" />
                            {business.shipping}
                          </div>
                        </div>
                      </div>

                      {/* Action */}
                      <button onClick={() => toggleExpand(business.id)} className={`p-2 rounded-full transition-colors self-end sm:self-center ${dk.textMuted} hover:${dk.surfaceMuted}`} data-id="element-9834" data-mp="2es8rrygzu077">
                        {isExpanded ? <ChevronUpIcon className="h-5 w-5" data-id="element-9835" data-mp="20thgskxfguna" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-9836" data-mp="1vzn1rusrp3uj" />}
                      </button>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && <div className={`border-t p-4 sm:p-6 ${dk.cardSubtle}`} data-id="element-9837" data-mp="13apusnu0ath3">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-9838" data-mp="3eykxcd6pdxjc">
                          {/* Left Column: Description & Content */}
                          <div className="lg:col-span-2 space-y-6" data-id="element-9839" data-mp="352hlugpyomjq">
                            <div data-id="element-9840" data-mp="3a0svorg9ax1y">
                              <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${dk.textMuted}`} data-id="element-9841" data-mp="3a018a3wtc5mr">
                                About
                              </h4>
                              <p className={`text-sm leading-relaxed ${dk.textSecondary}`} data-id="element-9842" data-mp="6hzttbwubwnm">
                                {business.description}
                              </p>
                            </div>

                            <div data-id="element-9843" data-mp="2d8z4jpcb4iig">
                              <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${dk.textMuted}`} data-id="element-9844" data-mp="2hc5ilj5nvdzh">
                                Categories
                              </h4>
                              <div className="flex flex-wrap gap-2" data-id="element-9845" data-mp="33zu50e9ojn34">
                                {business.categories.map(cat => <span key={cat} className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${rt.badge} ${rt.borderActive}`} data-id="element-9846" data-mp="2uzxe05ztic4w">
                                    {cat}
                                  </span>)}
                              </div>
                            </div>

                            {/* Featured Product Preview */}
                            {business.featuredProducts && business.featuredProducts.length > 0 && <div className={`mt-4 p-3 rounded-lg border ${dk.card}`} data-id="element-9847" data-mp="ahb3hir79zmj">
                                  <div className="flex items-center gap-3" data-id="element-9848" data-mp="ms29jnup5cqr">
                                    <img src={business.featuredProducts[0].image} alt={business.featuredProducts[0].name} className="w-12 h-12 rounded object-cover" data-id="element-9849" data-mp="mu5ah2xe0qa9" />

                                    <div className="flex-1 min-w-0" data-id="element-9850" data-mp="15qwsl9170zcq">
                                      {(() => {
                              const badgeConfig = getBadgeConfig(business.featuredProducts[0].badge || '', darkMode);
                              const BadgeIcon = badgeConfig.icon;
                              return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${badgeConfig.colors}`} data-id="element-9851" data-mp="2thkldzw372ba">
                                            <BadgeIcon className="h-3 w-3" data-id="element-9852" data-mp="38671w4xga043" />
                                            {badgeConfig.label}
                                          </span>;
                            })()}
                                      <p className={`text-sm font-medium mt-1 ${dk.textPrimary}`} data-id="element-9853" data-mp="2khxkdf79ryir">
                                        {business.featuredProducts[0].name}
                                      </p>
                                      <p className={`text-xs ${dk.textMuted}`} data-id="element-9854" data-mp="4q9qxtrgso4p">
                                        $
                                        {business.featuredProducts[0].price.toFixed(2)}
                                      </p>
                                    </div>
                                  </div>
                                </div>}
                          </div>

                          {/* Right Column: Details & CTA */}
                          <div className={`lg:border-l pl-0 lg:pl-8 space-y-5 ${dk.border}`} data-id="element-9855" data-mp="15igy61bvsm4e">
                            {/* Trust Signals */}
                            <div data-id="element-9856" data-mp="wlq39s92fdbe">
                              <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${dk.textMuted}`} data-id="element-9857" data-mp="rlbqxy0flh7k">
                                Trust & Activity
                              </h4>
                              <div className="space-y-2.5" data-id="element-9858" data-mp="2lwqi8j93bopf">
                                <div className={`flex items-center gap-2.5 text-sm ${dk.textSecondary}`} data-id="element-9859" data-mp="k8ma6vkyezn9">
                                  <div className={`p-1.5 rounded-md ${darkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`} data-id="element-9860" data-mp="abzrodg3okoe">
                                    <ShoppingBagIcon className={`h-3.5 w-3.5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-9861" data-mp="130ew661lvklh" />
                                  </div>
                                  <span className="font-semibold" data-id="element-9862" data-mp="t1fvb9n30t91">
                                    {formatSalesCount(business.totalSales)}{' '}
                                    sales
                                  </span>
                                </div>
                                <div className={`flex items-center gap-2.5 text-sm ${dk.textSecondary}`} data-id="element-9863" data-mp="3irjil5tatr0a">
                                  <div className={`p-1.5 rounded-md ${rt.bgMedium}`} data-id="element-9864" data-mp="1vujz66cp3du3">
                                    <CalendarIcon className={`h-3.5 w-3.5 ${rt.textPrimary}`} data-id="element-9865" data-mp="29tt5meb3mtkv" />
                                  </div>
                                  <span data-id="element-9866" data-mp="1i3eugpugzvcw">
                                    {new Date().getFullYear() - business.memberSince}
                                    + years on LitLaunch
                                  </span>
                                </div>
                                {business.verified && <div className={`flex items-center gap-2.5 text-sm ${dk.textSecondary}`} data-id="element-9867" data-mp="yee9fcqps5qt">
                                    <div className={`p-1.5 rounded-md ${darkMode ? 'bg-emerald-900/30' : 'bg-emerald-50'}`} data-id="element-9868" data-mp="1733fgfkntq31">
                                      <ShieldCheckIcon className={`h-3.5 w-3.5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} data-id="element-9869" data-mp="1h19dun79ce35" />
                                    </div>
                                    <span className="font-medium" data-id="element-9870" data-mp="1qqj746du82su">
                                      Verified Small Business
                                    </span>
                                  </div>}
                              </div>
                            </div>

                            <div data-id="element-9871" data-mp="24ohgsyh7rb20">
                              <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${dk.textMuted}`} data-id="element-9872" data-mp="5dzlmw4b93dr">
                                Connect
                              </h4>
                              <div className="flex flex-col space-y-2" data-id="element-9873" data-mp="2ucrfhv70v05">
                                {business.social.website && <a href={business.social.website} target="_blank" rel="noopener noreferrer" className={`flex items-center text-sm ${dk.textSecondary} ${rt.textHover}`} data-id="element-9874" data-mp="3p8cwgm8l2u47">
                                    <GlobeIcon className="h-4 w-4 mr-2" data-id="element-9875" data-mp="gqq7qnnzqqd1" />{' '}
                                    Website
                                  </a>}
                                {business.social.instagram && <a href={`https://instagram.com/${business.social.instagram}`} target="_blank" rel="noopener noreferrer" className={`flex items-center text-sm ${dk.textSecondary} ${rt.textHover}`} data-id="element-9876" data-mp="2wl0949ulmi9u">
                                    <InstagramIcon className="h-4 w-4 mr-2" data-id="element-9877" data-mp="19o0udnoxwt9z" />{' '}
                                    Instagram
                                  </a>}
                                {business.social.tiktok && <a href={`https://tiktok.com/${business.social.tiktok}`} target="_blank" rel="noopener noreferrer" className={`flex items-center text-sm ${dk.textSecondary} ${rt.textHover}`} data-id="element-9878" data-mp="14o4m3c97mfom">
                                    <svg className="h-4 w-4 mr-2 fill-current" viewBox="0 0 24 24" data-id="element-9879" data-mp="h0wicyrfjeop">
                                      <path d="M19.321 5.562a5.122 5.122 0 01-3.414-5.444h-3.274v13.456c0 1.535-1.253 2.778-2.798 2.778s-2.798-1.243-2.798-2.778a2.79 2.79 0 012.798-2.778c.157 0 .31.013.462.04V7.562a6.008 6.008 0 00-.462-.019c-3.425 0-6.198 2.752-6.198 6.151 0 3.399 2.773 6.151 6.198 6.151 3.425 0 6.198-2.752 6.198-6.151V8.97a8.446 8.446 0 003.288 1.35v-3.276a5.144 5.144 0 01-2.798-1.482z" data-id="element-9880" data-mp="3ufnnvx9a8nlx" />
                                    </svg>{' '}
                                    TikTok
                                  </a>}
                              </div>
                            </div>

                            <div className="pt-2" data-id="element-9881" data-mp="q2h1xr30bb5r">
                              <Link to={`/vendor/${business.slug}`} className={`block w-full text-center py-2 rounded-lg font-medium transition-colors ${rt.primaryButton}`} data-id="element-9882" data-mp="14ypxoinov6df">
                                View Profile
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>;
            })}
            </div>

            {/* Empty State */}
            {filteredBusinesses.length === 0 && <div className={`text-center py-16 rounded-xl border border-dashed ${dk.card}`} data-id="element-9883" data-mp="2y3idxhrexewl">
                <ShoppingBagIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-9884" data-mp="331jzc9cb9qb8" />

                <h3 className={`text-lg font-medium mb-2 ${dk.textPrimary}`} data-id="element-9885" data-mp="3cr3f364q941v">
                  No merchandise businesses found
                </h3>
                <p className={`max-w-md mx-auto ${dk.textMuted}`} data-id="element-9886" data-mp="2fawll7fps6gr">
                  Try adjusting your search or filters to find what you're
                  looking for.
                </p>
                <button onClick={resetFilters} className={`mt-6 px-4 py-2 rounded-md transition-colors ${rt.primaryButton}`} data-id="element-9887" data-mp="11emxlakglhnr">
                  Clear Filters
                </button>
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default MerchBusinessProfiles;